const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, digits = 1) => `${Number(n ?? 0).toFixed(digits)}%`;
export const buildCtReadinessScore = (overview = {}) => {
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const completion = overview.completion_pct ?? 0;
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  return Math.round((aadhaar * 0.25 + completion * 0.25 + ctet * 0.25 + nishtha * 0.25) * 10) / 10;
};

export const buildCtReadinessGaugeStyle = (readiness = 0) => {
  const pct = Math.min(100, Math.max(0, Number(readiness) || 0));
  const color = pct >= 70 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
  return {
    background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${pct}%, rgba(245,158,11,.14) ${pct}% 100%)`,
    "--ctex-gauge-color": color,
  };
};

export const buildCtHeroHeadline = (overview = {}) => {
  const ctet = overview.ctet_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const completion = overview.completion_pct ?? 0;
  if (aadhaar >= 90 && completion >= 90 && ctet < 15) {
    return "Teacher readiness is data-strong, certification-weak";
  }
  if (ctet < 10) return "Certification gap is constraining teacher readiness";
  return "Teacher workforce readiness needs targeted acceleration";
};

export const buildCtHeroNarrative = (overview = {}) => {
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const completion = overview.completion_pct ?? 0;
  if (aadhaar >= 90 && completion >= 90) {
    return `Aadhaar verification and data completion are healthy, but CTET qualification (${fmtPct(ctet)}) and NISHTHA completion (${fmtPct(nishtha)}) need immediate intervention.`;
  }
  return `Aadhaar verification is at ${fmtPct(aadhaar)} and data completion at ${fmtPct(completion)}. CTET (${fmtPct(ctet)}) and NISHTHA (${fmtPct(nishtha)}) remain the priority improvement areas.`;
};

export const buildCtStatusChips = (overview = {}) => {
  const readiness = buildCtReadinessScore(overview);
  const ctet = overview.ctet_pct ?? 0;
  const blocks = overview.total_blocks ?? 0;
  const chips = [];
  if (readiness >= 70) chips.push({ label: "Green Readiness", className: "green" });
  else if (readiness >= 50) chips.push({ label: "Amber Readiness", className: "" });
  else chips.push({ label: "Red Readiness", className: "red" });
  if (ctet < 15) chips.push({ label: "CTET Gap Critical", className: "red" });
  if (blocks > 0) chips.push({ label: `${blocks} Blocks Covered`, className: "blue" });
  return chips;
};

export const buildCtPrimaryAction = (overview = {}, blockData = []) => {
  const blocks = [...(blockData || [])].sort((a, b) => (b.total_teachers || 0) - (a.total_teachers || 0));
  const top5 = blocks.slice(0, 5);
  if (!top5.length) {
    return "Run a block-wise certification completion sprint once block-level data is available.";
  }
  const total = overview.total_teachers || blocks.reduce((s, b) => s + (b.total_teachers || 0), 0) || 1;
  const topSum = top5.reduce((s, b) => s + (b.total_teachers || 0), 0);
  const pct = ((topSum / total) * 100).toFixed(1);
  const names = top5.map((b) => b.block_name).join(", ");
  return `Run a block-wise certification completion sprint for ${names}; these five blocks carry ${pct}% of the teacher base.`;
};

export const buildCtExecutiveKpis = (overview = {}) => {
  const total = overview.total_teachers ?? 0;
  const records = overview.teacher_records ?? overview.unique_teachers ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  return [
    {
      label: "Total Teachers",
      value: fmt(total),
      sub: records && records !== total ? `${fmt(records)} roster records` : `${fmt(overview.total_schools)} schools`,
      subClass: "",
    },
    {
      label: "Schools Covered",
      value: fmt(overview.total_schools),
      sub: `Avg ${overview.avg_teachers_per_school ?? 0} / school`,
      subClass: "",
    },
    {
      label: "Aadhaar Verified",
      value: fmtPct(overview.aadhaar_verified_pct),
      sub: (overview.aadhaar_verified_pct ?? 0) >= 90 ? "Healthy identity quality" : "Verification push needed",
      subClass: "",
    },
    {
      label: "Data Completion",
      value: fmtPct(overview.completion_pct),
      sub: (overview.completion_pct ?? 0) >= 95 ? "Strong reporting base" : "Complete pending records",
      subClass: "",
    },
    {
      label: "NISHTHA Done",
      value: fmtPct(nishtha),
      sub: nishtha >= 70 ? "Training on track" : "Needs acceleration",
      subClass: nishtha >= 70 ? "" : "warning-text",
    },
  ];
};

export const buildCtComplianceRings = (overview = {}) => [
  {
    label: "Aadhaar Verified",
    value: overview.aadhaar_verified_pct ?? 0,
    display: fmtPct(overview.aadhaar_verified_pct),
    ringClass: "",
    description: "Teacher identity verification is strong. Focus only on blocks below 90%.",
  },
  {
    label: "Data Completion",
    value: overview.completion_pct ?? 0,
    display: fmtPct(overview.completion_pct),
    ringClass: "",
    description: "Data foundation is reliable enough for block-level monitoring.",
  },
  {
    label: "CTET Qualified",
    value: overview.ctet_pct ?? 0,
    display: fmtPct(overview.ctet_pct),
    ringClass: "red",
    description: "Lowest readiness indicator. Needs leadership-level review.",
  },
  {
    label: "NISHTHA Completed",
    value: overview.nishtha_pct ?? 0,
    display: fmtPct(overview.nishtha_pct),
    ringClass: "orange",
    description: "Training adoption is below desired maturity and should be tracked weekly.",
  },
];

export const buildCtGenderDonut = (overview = {}, genderData = []) => {
  const total = overview.total_teachers ?? 0;
  const female = overview.female_count ?? 0;
  const male = overview.male_count ?? 0;
  const otherEntry = (genderData || []).find(
    (g) => g.gender === "Other" || g.gender === "Transgender"
  );
  const other = otherEntry?.count ?? Math.max(0, total - female - male);
  const femalePct = total > 0 ? (female / total) * 100 : 0;
  const malePct = total > 0 ? (male / total) * 100 : 0;
  const otherPct = total > 0 ? (other / total) * 100 : 0;
  const maleEnd = femalePct + malePct;
  return {
    total,
    gpi: overview.gender_parity_index ?? 0,
    style: {
      background: `conic-gradient(#ec4899 0 ${femalePct}%, #2563eb ${femalePct}% ${maleEnd}%, #64748b ${maleEnd}% 100%)`,
    },
    legend: [
      { label: "Female Teachers", value: female, pct: femalePct, dotClass: "" },
      { label: "Male Teachers", value: male, pct: malePct, dotClass: "blue" },
      { label: "Other", value: other, pct: otherPct, dotClass: "gray", hidePct: other <= 1 },
    ],
  };
};

export const buildCtBlockBars = (blockData = []) => {
  const top = [...(blockData || [])].slice(0, 10);
  const max = top[0]?.total_teachers || 1;
  return top.map((b) => ({
    block: b.block_name,
    teachers: b.total_teachers ?? 0,
    width: Math.max(8, ((b.total_teachers ?? 0) / max) * 100),
    display: fmt(b.total_teachers),
  }));
};

export const buildCtExecutiveInsights = (overview = {}, blockData = []) => {
  const ctet = overview.ctet_pct ?? 0;
  const nishtha = overview.nishtha_pct ?? 0;
  const aadhaar = overview.aadhaar_verified_pct ?? 0;
  const completion = overview.completion_pct ?? 0;
  const blocks = [...(blockData || [])].sort((a, b) => (b.total_teachers || 0) - (a.total_teachers || 0));
  const top5 = blocks.slice(0, 5);
  const total = overview.total_teachers || 1;
  const topSum = top5.reduce((s, b) => s + (b.total_teachers || 0), 0);
  const loadPct = ((topSum / total) * 100).toFixed(1);
  const topNames = top5.map((b) => b.block_name).join(", ");
  return [
    {
      id: "cert-gap",
      title: "Certification Gap Is the Biggest Risk",
      body: `CTET qualification is only ${fmtPct(ctet)}, while NISHTHA completion is ${fmtPct(nishtha)}. Treat this as the main executive intervention area.`,
      iconClass: "red",
    },
    {
      id: "data-quality",
      title: "Data Quality Is Mature",
      body:
        aadhaar >= 90 && completion >= 90
          ? "Aadhaar verification and data completion are above 90%, giving the department a credible base for targeted action."
          : `Aadhaar verification (${fmtPct(aadhaar)}) and data completion (${fmtPct(completion)}) provide a baseline for block-level monitoring.`,
      iconClass: "green",
    },
    {
      id: "block-load",
      title: "Top 5 Blocks Drive Almost Half the Load",
      body: top5.length
        ? `${topNames} together account for ${fmt(topSum)} teacher records, or ${loadPct}% of the total.`
        : "Block load concentration data will appear once block-wise records are available.",
      iconClass: "purple",
    },
  ];
};

export const buildCtBlockTableRows = (blockData = []) =>
  (blockData || []).map((block) => {
    const aadhaar = block.aadhaar_pct ?? 0;
    let status = "Healthy";
    let statusClass = "";
    if (aadhaar < 90) {
      status = "Watch";
      statusClass = "warn";
    }
    let trackClass = "";
    if (aadhaar < 90) trackClass = "orange";
    else if (aadhaar < 70) trackClass = "red";
    return {
      rank: block.rank,
      blockCode: block.block_code,
      blockName: block.block_name,
      teachers: block.total_teachers,
      schools: block.total_schools,
      avgSchool: block.avg_per_school,
      femalePct: block.female_pct,
      gpi: block.gpi,
      aadhaarPct: aadhaar,
      aadhaarDisplay: fmtPct(aadhaar),
      status,
      statusClass,
      trackClass,
    };
  });

export const buildCtTableMeta = (overview = {}, blockData = []) => ({
  blockCount: overview.total_blocks ?? blockData.length,
  recordCount: overview.teacher_records ?? overview.total_teachers ?? 0,
});

export const buildCTTeacherExecutiveMarkdown = (overview = {}, blockData = []) => {
  const insights = buildCtExecutiveInsights(overview, blockData);
  return [
    "## Insights",
    `- ${buildCtHeroHeadline(overview)}`,
    `- ${buildCtHeroNarrative(overview)}`,
    ...insights.map((i) => `- ${i.title}: ${i.body}`),
    "",
    "## Priority Action Items",
    `- ${buildCtPrimaryAction(overview, blockData)}`,
  ].join("\n");
};
